import {
	Button,
	Chip,
	ChipConfirmModal,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	EmptyState,
	Input,
	Label,
	NativeSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type TrafficGoal } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { brand, settings } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { SourceNote } from "~/modules/analytics/pages/tracking/shared";

export function GoalsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { editor, why } = useAccess();
	const [adding, setAdding] = useState(false);
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "goals", filters.range],
		queryFn: () => api.trafficGoals(entityId, { range: filters.range }),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="goals"
			lede={t("goals.lede")}
			filters={filters}
			setFilters={setFilters}
			actions={
				<Chip
					variant="primary"
					leftIcon={Plus}
					type="button"
					disabled={!editor}
					title={whyDisabled(editor, why.editor)}
					onClick={() => setAdding(true)}
				>
					{t("goals.add")}
				</Chip>
			}
		>
			<Async state={state}>
				{(data) => (
					<>
						<SourceNote frame={data} />
						{!data.available || data.setup_required ? (
							<p className="hint mb-(--s-8)">
								{t(data.source === "ga4" ? "goals.gaNotice" : "goals.installNotice")}{" "}
								<Link className="drill" to={settings(entityId, "tracking")}>
									{t("traffic.noSourceAction")}
								</Link>
							</p>
						) : null}
						{data.goals.length === 0 ? (
							<div className="stack py-(--s-8)">
								<h2>{t("goals.empty")}</h2>
								<p className="muted">{t("goals.firstBody")}</p>
								<p className="hint">{t("goals.optional")}</p>
							</div>
						) : (
							<Rows entityId={entityId} goals={data.goals} counted={data.available && !data.setup_required} />
						)}
						{data.goals.length > 0 ? (
							<div className="row-actions mt-(--s-8)">
								<Link className="drill" to={`${brand(entityId)}/reflow/attribution`}>
									{t("goals.viewAttribution")}
								</Link>
								<Link className="drill" to={`${brand(entityId)}/reflow/live`}>
									{t("traffic.start.verify")}
								</Link>
							</div>
						) : null}
						{adding ? <AddGoal entityId={entityId} onClose={() => setAdding(false)} /> : null}
					</>
				)}
			</Async>
		</StageShell>
	);
}

function Rows({ entityId, goals, counted }: { entityId: string; goals: TrafficGoal[]; counted: boolean }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const queryClient = useQueryClient();
	const [removing, setRemoving] = useState<TrafficGoal | null>(null);

	const remove = useMutation({
		mutationFn: (goal: string) => api.removeTrafficGoal(entityId, goal),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
			toast.success(t("goals.removed"));
		},
		onError: (error: unknown) => toast.error(error instanceof ApiError ? error.localized(locale) : String(error)),
	});

	if (goals.length === 0) return <EmptyState size="compact" title={t("goals.empty")} />;

	return (
		<>
			<div className="table-scroll">
				<Table variant="resource">
					<TableHeader>
						<TableRow>
							<TableHead className="wrap">{t("goals.name")}</TableHead>
							<TableHead className="wrap">{t("goals.match")}</TableHead>
							<TableHead className="num">{t("traffic.conversions")}</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{goals.map((goal) => (
							<TableRow key={goal.id}>
								<TableCell className="wrap">{goal.name}</TableCell>
								<TableCell className="wrap">
									<span className="faint">{t(`goals.kind.${goal.kind}`)}</span>{" "}
									<span className="mono">{goal.value}</span>
								</TableCell>
								{/* GA gives no event rows, so a count here would be a
								    zero that means "not measured". The dash says so. */}
								<TableCell className="num">{counted ? fmt.number(locale, goal.conversions) : "—"}</TableCell>
								<TableCell className="num">
									<Button
										type="button"
										size="sm"
										variant="destructive"
										disabled={!editor || remove.isPending}
										title={whyDisabled(editor, why.editor)}
										onClick={() => setRemoving(goal)}
									>
										{t("common.remove")}
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			{remove.error ? (
				<p role="alert">{remove.error instanceof ApiError ? remove.error.localized(locale) : String(remove.error)}</p>
			) : null}
			<ChipConfirmModal
				open={removing !== null}
				onOpenChange={(open) => !open && setRemoving(null)}
				title={t("goals.removeTitle")}
				text={t("goals.removeBody", { name: removing?.name ?? "" })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("common.remove"),
					variant: "destructive",
					onClick: () => {
						if (removing) remove.mutate(removing.id);
						setRemoving(null);
					},
				}}
			/>
		</>
	);
}

function AddGoal({ entityId, onClose }: { entityId: string; onClose: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const queryClient = useQueryClient();
	const [name, setName] = useState("");
	const [kind, setKind] = useState("path");
	const [value, setValue] = useState("");

	const add = useMutation({
		mutationFn: () => api.addTrafficGoal(entityId, { name: name.trim(), kind, value: value.trim() }),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
			setName("");
			setValue("");
			toast.success(t("goals.added"));
			onClose();
		},
		onError: (error: unknown) => toast.error(error instanceof ApiError ? error.localized(locale) : String(error)),
	});

	const invalidPath =
		kind === "path" &&
		value.trim() !== "" &&
		(!value.trim().startsWith("/") || value.trim() === "/" || value.trim().startsWith("//"));
	const canSave = editor && !add.isPending && name.trim() && value.trim() && !invalidPath;
	return (
		<ChipModal
			open
			onOpenChange={(open) => !open && !add.isPending && onClose()}
			dismissDisabled={add.isPending}
			srTitle={t("goals.add")}
		>
			<ChipModalHeader onClose={onClose} closeAriaLabel={t("common.close")}>
				{t("goals.add")}
			</ChipModalHeader>
			<ChipModalBody className="px-(--s-8)">
				<form
					id="traffic-goal-form"
					onSubmit={(event) => {
						event.preventDefault();
						if (canSave) add.mutate();
					}}
				>
					<fieldset
						disabled={!editor || add.isPending}
						title={whyDisabled(editor, why.editor)}
						className="stack bare-fieldset"
					>
						<div className="field">
							<Label htmlFor="goal-name">{t("goals.name")}</Label>
							<Input
								id="goal-name"
								value={name}
								onChange={(event) => setName(event.target.value)}
								placeholder={t("goals.namePlaceholder")}
							/>
						</div>
						<div className="field">
							<Label htmlFor="goal-value">{t("goals.match")}</Label>
							<div className="row">
								<NativeSelect
									id="goal-kind"
									value={kind}
									aria-label={t("goals.match")}
									onChange={(event) => {
										setKind(event.target.value);
										setValue("");
									}}
								>
									<option value="path">{t("goals.kind.path")}</option>
									<option value="event">{t("goals.kind.event")}</option>
								</NativeSelect>
								<Input
									id="goal-value"
									className="font-code grow"
									value={value}
									onChange={(event) => setValue(event.target.value)}
									placeholder={kind === "path" ? "/signup/success" : "signup"}
									aria-invalid={invalidPath}
									aria-describedby="goal-rule-hint"
								/>
							</div>
							<span id="goal-rule-hint" className="hint">
								{kind === "path" ? t("goals.pathHint") : t("goals.eventHint")}
							</span>
						</div>
						{invalidPath ? <p role="alert">{t("goals.invalidPath")}</p> : null}
						{kind === "event" && value.trim() ? (
							<pre className="mono whitespace-pre-wrap break-all">{`window.ondit?.track(${JSON.stringify(value.trim())});`}</pre>
						) : null}
						{add.error ? (
							<p role="alert">{add.error instanceof ApiError ? add.error.localized(locale) : String(add.error)}</p>
						) : null}
					</fieldset>
				</form>
			</ChipModalBody>
			<ChipModalFooter
				onCancel={onClose}
				cancelLabel={t("common.cancel")}
				primaryAction={{
					label: t(add.isPending ? "goals.saving" : "goals.add"),
					type: "submit",
					form: "traffic-goal-form",
					disabled: !canSave,
				}}
			/>
		</ChipModal>
	);
}
